import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Mail, TrendingUp, Target, ArrowUp, ArrowDown, RefreshCw, Bell, AlertTriangle, AlertCircle, Info, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import WeeklyTrendsChart from "@/components/WeeklyTrendsChart";
import TopMetricsRow from "@/components/TopMetricsRow";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentlyUpdatedLeads, setRecentlyUpdatedLeads] = useState([]);
  const [followUpStats, setFollowUpStats] = useState(null);
  const [weeklyTrends, setWeeklyTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStages, setLoadingStages] = useState({
    stats: true,
    followUp: true,
    recentLeads: true,
    recentUpdates: true,
    weeklyTrends: true
  });
  const [syncing, setSyncing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [fetchError, setFetchError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const navigate = useNavigate();

  // Auto-refresh when tab becomes visible (user returns after being idle)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if it's been more than 5 minutes since last refresh
        const timeSinceRefresh = (new Date() - lastRefresh) / 1000 / 60;
        if (timeSinceRefresh > 5) {
          console.log('Tab visible after idle - refreshing dashboard data');
          fetchDashboardData();
          setLastRefresh(new Date());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastRefresh]);

  // Auto-refresh every 10 minutes while tab is active
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('Auto-refreshing dashboard data (10 min interval)');
        fetchDashboardData();
        setLastRefresh(new Date());
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, []);

  // Helper function to get badge variant based on status
  const getStatusBadgeVariant = (status) => {
    switch(status) {
      case 'converted':
        return 'success';
      case 'existing_customer':
        return 'warning'; // Orange color for existing customers needing follow-up
      case 'contacted':
      case 'qualified':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Helper function to format status label
  const formatStatusLabel = (status) => {
    if (status === 'existing_customer') {
      return 'Existing Customer';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Read location ID once on mount (Layout key={currentLocation} guarantees fresh mount per location)
  const [locationId] = useState(() => localStorage.getItem('currentLocationId') || '');
  
  useEffect(() => {
    // IMMEDIATELY show cached data from previous visit to this location
    let hasCachedData = false;
    try {
      const cachedStats = localStorage.getItem(`swr_stats_${locationId}`);
      if (cachedStats) { setStats(JSON.parse(cachedStats)); hasCachedData = true; }
      const cachedFollowUp = localStorage.getItem(`swr_followup_${locationId}`);
      if (cachedFollowUp) { setFollowUpStats(JSON.parse(cachedFollowUp)); hasCachedData = true; }
      const cachedLeads = localStorage.getItem(`swr_recent_${locationId}`);
      if (cachedLeads) { setRecentLeads(JSON.parse(cachedLeads)); hasCachedData = true; }
      const cachedTrends = localStorage.getItem(`swr_trends_${locationId}`);
      if (cachedTrends) { setWeeklyTrends(JSON.parse(cachedTrends)); hasCachedData = true; }
    } catch (e) { /* ignore corrupt cache */ }
    
    // If we have cached data, show it immediately (stop showing skeleton)
    if (hasCachedData) {
      setLoading(false);
      setLoadingStages({ stats: false, followUp: false, recentLeads: false, recentUpdates: true, weeklyTrends: false });
    }
    
    // Then fetch fresh data in background
    fetchDashboardData();
  }, [locationId]);

  const fetchDashboardData = async (isRetry = false) => {
    if (!isRetry) setFetchError(null);
    
    const locId = localStorage.getItem('currentLocationId');
    if (!locId) {
      setFetchError('No location selected. Please switch locations.');
      setLoading(false);
      setLoadingStages({ stats: false, followUp: false, recentLeads: false, recentUpdates: false, weeklyTrends: false });
      return;
    }
    
    // Fetch integration status / system alerts
    axios.get(`${API}/integrations/status`)
      .then(res => {
        setSystemAlerts(res.data.alerts || []);
      })
      .catch(() => {});
    
    // Explicit location_id param ensures correct data even if header is stale
    const locParam = `location_id=${encodeURIComponent(locId)}`;
    let completedCount = 0;
    const totalCalls = 5;
    const markComplete = () => { completedCount++; if (completedCount >= totalCalls) setLoading(false); };
    
    try {
      axios.get(`${API}/leads/stats/dashboard?${locParam}`)
        .then(res => {
          setStats(res.data);
          try { localStorage.setItem(`swr_stats_${locId}`, JSON.stringify(res.data)); } catch(e) {}
          setFetchError(null);
          setRetryCount(0);
          setLoadingStages(prev => ({ ...prev, stats: false }));
        })
        .catch(err => {
          setLoadingStages(prev => ({ ...prev, stats: false }));
          if ((err.response?.status >= 500 || !err.response) && retryCount < 2) {
            setRetryCount(prev => prev + 1);
            setTimeout(() => fetchDashboardData(true), 2000);
          } else if (err.response?.status !== 401) {
            setFetchError('Unable to load dashboard data. Click Refresh to retry.');
          }
        })
        .finally(markComplete);
      
      axios.get(`${API}/leads/stats/follow-up?${locParam}`)
        .then(res => {
          setFollowUpStats(res.data);
          try { localStorage.setItem(`swr_followup_${locId}`, JSON.stringify(res.data)); } catch(e) {}
          setLoadingStages(prev => ({ ...prev, followUp: false }));
        })
        .catch(() => { setLoadingStages(prev => ({ ...prev, followUp: false })); })
        .finally(markComplete);
      
      axios.get(`${API}/leads?limit=6&${locParam}`)
        .then(res => {
          setRecentLeads(res.data);
          try { localStorage.setItem(`swr_recent_${locId}`, JSON.stringify(res.data)); } catch(e) {}
          setLoadingStages(prev => ({ ...prev, recentLeads: false }));
        })
        .catch(() => { setLoadingStages(prev => ({ ...prev, recentLeads: false })); })
        .finally(markComplete);
      
      axios.get(`${API}/leads/recent-updates?limit=10&${locParam}`)
        .then(res => { setRecentlyUpdatedLeads(res.data); setLoadingStages(prev => ({ ...prev, recentUpdates: false })); })
        .catch(() => { setRecentlyUpdatedLeads([]); setLoadingStages(prev => ({ ...prev, recentUpdates: false })); })
        .finally(markComplete);
      
      axios.get(`${API}/leads/stats/weekly-trends?${locParam}`)
        .then(res => {
          setWeeklyTrends(res.data);
          try { localStorage.setItem(`swr_trends_${locId}`, JSON.stringify(res.data)); } catch(e) {}
          setLoadingStages(prev => ({ ...prev, weeklyTrends: false }));
        })
        .catch(() => { setLoadingStages(prev => ({ ...prev, weeklyTrends: false })); })
        .finally(markComplete);
      
    } catch (error) {
      setLoading(false);
      setFetchError('Failed to load dashboard. Click Refresh to retry.');
    }
  };

  const handleSyncEmails = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/gmail/sync-emails`);
      toast.success(response.data.message);
      // Refresh dashboard after sync
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync emails");
    } finally {
      setSyncing(false);
    }
  };

  // Skeleton loader component
  const SkeletonCard = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
        </div>
      </CardContent>
    </Card>
  );

  const SkeletonChart = () => (
    <Card>
      <CardHeader>
        <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
      </CardHeader>
      <CardContent>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </CardContent>
    </Card>
  );

  const statusCounts = stats?.status_counts || {};
  const totalLeads = stats?.total_leads || 0;

  return (
    <div className="ops-v3 space-y-8" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-[32px] font-extrabold tracking-tight text-gray-900 mb-1" style={{ fontFamily: 'Manrope' }}>Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-500">Track your lead generation and conversion metrics</p>
        </div>
        <Button
          onClick={handleSyncEmails}
          disabled={syncing}
          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
          data-testid="sync-button"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? "Syncing..." : "Sync Emails"}
        </Button>
      </div>

      {/* Error Banner */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between" data-testid="dashboard-error-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-red-700">{fetchError}</span>
          </div>
          <Button
            onClick={() => {
              setFetchError(null);
              setLoadingStages({ stats: true, followUp: true, recentLeads: true, recentUpdates: true, weeklyTrends: true });
              setRetryCount(0);
              fetchDashboardData();
            }}
            variant="outline"
            size="sm"
            className="text-red-700 border-red-300 hover:bg-red-100"
          >
            Retry
          </Button>
        </div>
      )}

      {/* System Alerts - Critical integration warnings */}
      {systemAlerts.length > 0 && systemAlerts.map((alert) => (
        <div
          key={alert.alert_type}
          className={`rounded-lg p-4 flex items-start sm:items-center justify-between gap-3 ${
            alert.severity === 'critical'
              ? 'bg-red-50 border border-red-300'
              : 'bg-amber-50 border border-amber-300'
          }`}
          data-testid={`system-alert-${alert.alert_type}`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${
              alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
            }`} />
            <div>
              <p className={`text-sm font-semibold ${
                alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'
              }`}>
                Integration Alert
              </p>
              <p className={`text-sm ${
                alert.severity === 'critical' ? 'text-red-700' : 'text-amber-700'
              }`}>
                {alert.message}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
              </p>
            </div>
          </div>
          <Button
            onClick={async () => {
              try {
                await axios.post(`${API}/integrations/alerts/${alert.alert_type}/dismiss`);
                setSystemAlerts(prev => prev.filter(a => a.alert_type !== alert.alert_type));
                toast.success('Alert dismissed');
              } catch {
                toast.error('Failed to dismiss alert');
              }
            }}
            variant="outline"
            size="sm"
            className={`shrink-0 ${
              alert.severity === 'critical'
                ? 'text-red-700 border-red-300 hover:bg-red-100'
                : 'text-amber-700 border-amber-300 hover:bg-amber-100'
            }`}
            data-testid={`dismiss-alert-${alert.alert_type}`}
          >
            Dismiss
          </Button>
        </div>
      ))}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
        {loadingStages.stats ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
        <Card className="card" data-testid="total-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Total Leads</CardTitle>
            <Users className="h-5 w-5 text-cyan-500" />
          </CardHeader>
          <CardContent>
            <div className="v3-num text-3xl font-bold text-gray-900">{totalLeads}</div>
            <p className="text-xs text-gray-500 mt-2">All time</p>
          </CardContent>
        </Card>

        <Card className="card" data-testid="conversion-rate-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Conversion Rate</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="v3-num text-3xl font-bold text-gray-900">{stats?.conversion_rate || 0}%</div>
            <div className="text-xs text-green-600 mt-2 flex items-center">
              <ArrowUp className="h-3 w-3 mr-1" />Converting well
            </div>
          </CardContent>
        </Card>

        <Card className="card" data-testid="new-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[11px] font-bold uppercase tracking-wide text-gray-500">New Leads</CardTitle>
            <Mail className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="v3-num text-3xl font-bold text-gray-900">{statusCounts.new || 0}</div>
            <p className="text-xs text-gray-500 mt-2">Awaiting contact</p>
          </CardContent>
        </Card>

        <Card className="card border-l-4 border-l-orange-500" data-testid="existing-customer-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Existing Customer</CardTitle>
            <Bell className="h-5 w-5 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="v3-num text-3xl font-bold text-gray-900">{statusCounts.existing_customer || 0}</div>
            <p className="text-xs text-orange-600 mt-2 font-medium">Require follow-up</p>
          </CardContent>
        </Card>

        <Card className="card" data-testid="converted-leads-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Converted</CardTitle>
            <Target className="h-5 w-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="v3-num text-3xl font-bold text-gray-900">{statusCounts.converted || 0}</div>
            <p className="text-xs text-gray-500 mt-2">Closed deals</p>
          </CardContent>
        </Card>
          </>
        )}
      </div>

      {/* Follow-Up Required Section - Always Show */}
      {loadingStages.followUp ? (
        <SkeletonChart />
      ) : followUpStats && (
        <Card className={`card border-l-4 ${followUpStats.total_needs_followup > 0 ? 'border-l-orange-500' : 'border-l-green-500'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-[10px] grid place-items-center shrink-0" style={{ background: "#E6F6F3" }}><Bell className="h-[18px] w-[18px]" style={{ color: "#1F8A7C" }} strokeWidth={2.25} /></span>
                <div>
                  <CardTitle className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                    Follow-Up Status
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    {followUpStats.total_needs_followup > 0 
                      ? `${followUpStats.total_needs_followup} lead${followUpStats.total_needs_followup !== 1 ? 's' : ''} need${followUpStats.total_needs_followup === 1 ? 's' : ''} attention`
                      : '✅ No follow-ups required - all caught up!'
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/leads')}
                className="bg-[#1F8A7C] hover:bg-[#18705F] text-white"
                data-testid="dashboard-view-leads-btn"
              >
                View Leads
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {followUpStats.total_needs_followup > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Urgent - Modern gradient style */}
                <div className="bg-white border border-gray-200 text-gray-900 rounded-xl p-5 v3-tint-neg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Urgent</p>
                      <p className="v3-num text-4xl font-bold mt-1">{followUpStats.urgent}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">No contact yet</p>
                </div>

                {/* Warning - Modern gradient style */}
                <div className="bg-white border border-gray-200 text-gray-900 rounded-xl p-5 v3-tint-warn">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Warning</p>
                      <p className="v3-num text-4xl font-bold mt-1">{followUpStats.warning}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">2+ days ago</p>
                </div>

                {/* Queue - Modern gradient style */}
                <div className="bg-white border border-gray-200 text-gray-900 rounded-xl p-5 v3-tint-pos">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Queue</p>
                      <p className="v3-num text-4xl font-bold mt-1">{followUpStats.queue || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <Info className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Recent contact</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center p-8 bg-green-50 rounded-lg border border-green-200">
                <div className="text-center">
                  <div className="text-5xl mb-3">🎉</div>
                  <div className="text-lg font-semibold text-green-700 mb-1">All Caught Up!</div>
                  <p className="text-sm text-green-600">No leads currently need follow-up. Great work!</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Row: Lead Funnel, Services, and Sources */}
      <TopMetricsRow stats={stats} totalLeads={totalLeads} />

      {/* Weekly Trends Chart */}
      {loadingStages.weeklyTrends ? (
        <SkeletonChart />
      ) : (
        <WeeklyTrendsChart weeklyData={weeklyTrends} />
      )}

      {/* Two Column Layout: Activity and Recent Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recently Updated */}
        <Card className="card h-full">
            <CardHeader>
              <CardTitle className="text-lg font-extrabold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Clock className="h-5 w-5" />
                Recently Updated
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentlyUpdatedLeads && recentlyUpdatedLeads.length > 0 ? (
                <div className="space-y-3">
                  {recentlyUpdatedLeads.slice(0, 5).map((lead) => {
                    const lastAction = lead.action_log && lead.action_log.length > 0 
                      ? lead.action_log[lead.action_log.length - 1] 
                      : null;
                    
                    // Format last activity message
                    let lastActivityText = 'None';
                    if (lastAction) {
                      const actionDate = new Date(lastAction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      const actionType = lastAction.type.charAt(0).toUpperCase() + lastAction.type.slice(1);
                      
                      if (lastAction.type === 'call') {
                        lastActivityText = `Called on ${actionDate}`;
                      } else if (lastAction.type === 'email') {
                        lastActivityText = `Emailed on ${actionDate}`;
                      } else if (lastAction.type === 'comment' && lastAction.text) {
                        const notePreview = lastAction.text.length > 30 
                          ? lastAction.text.substring(0, 30) + '...' 
                          : lastAction.text;
                        lastActivityText = `Notes: ${notePreview}`;
                      } else {
                        lastActivityText = `${actionType} on ${actionDate}`;
                      }
                    }
                    
                    return (
                      <div 
                        key={lead.id} 
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="font-medium text-gray-900 truncate">
                              {lead.name || 'Unknown'}
                            </span>
                            <Badge variant={getStatusBadgeVariant(lead.status)} className="text-xs">
                              {formatStatusLabel(lead.status)}
                            </Badge>
                            {lead.service && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {lead.service}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                            <div className="flex items-center gap-1 text-gray-600">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{lead.email || lead.canonical_email || 'No email'}</span>
                            </div>
                            {lead.phone && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <span>📞</span>
                                <span>{lead.phone}</span>
                              </div>
                            )}
                            {lead.company && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <span>🏢</span>
                                <span className="truncate">{lead.company}</span>
                              </div>
                            )}
                            {lead.source && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <span>📍</span>
                                <span className="truncate">{lead.source}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-xs bg-white p-2 rounded border border-gray-200 mb-1">
                            <span className="font-medium text-gray-700">Last Activity: </span>
                            <span className="text-gray-600">{lastActivityText}</span>
                            {lastAction && (
                              <span className="text-gray-500 ml-2">
                                by {lastAction.user_name || lastAction.user_email || lead.updated_by_name || 'System'}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-xs text-gray-500 flex items-center justify-between">
                            <span>Updated by <span className="font-medium">{lead.updated_by_name || lead.updated_by || 'System'}</span></span>
                            <span>{new Date(lead.updated_at).toLocaleDateString()} at {new Date(lead.updated_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No recent updates yet</p>
              )}
            </CardContent>
          </Card>

        {/* Recent Leads */}
        <Card className="card h-full">
          <CardHeader>
            <CardTitle className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope' }}>Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {recentLeads.length > 0 ? (
              <div className="space-y-3">
                {recentLeads.map((lead) => {
                  const daysAgo = Math.floor((new Date() - new Date(lead.received_at || lead.created_at)) / (1000 * 60 * 60 * 24));
                  const timeAgo = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`;
                  
                  return (
                    <div 
                      key={lead.id} 
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="font-medium text-gray-900 truncate">
                            {lead.name || lead.canonical_email || "Unknown"}
                          </span>
                          <Badge variant={getStatusBadgeVariant(lead.status)} className="text-xs">
                            {formatStatusLabel(lead.status)}
                          </Badge>
                          {lead.service && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {lead.service}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex items-center gap-1 text-gray-600">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{lead.email || lead.canonical_email || 'No email'}</span>
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <span>📞</span>
                              <span>{lead.phone}</span>
                            </div>
                          )}
                          {lead.company && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <span>🏢</span>
                              <span className="truncate">{lead.company}</span>
                            </div>
                          )}
                          {lead.source && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <span>📍</span>
                              <span className="truncate">{lead.source}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500 mt-2 flex items-center justify-between">
                          <span className="font-medium text-cyan-600">{timeAgo}</span>
                          <span>{new Date(lead.received_at || lead.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No leads yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
